import { ref, onMounted, onUnmounted, nextTick, watch, isRef, type Ref, type ComputedRef } from 'vue';

interface Section {
  slug: string;
  sectionHeading?: string;
}

interface Options {
  scrollOffset?: number; // spacing for sticky headers
  rootMarginTop?: string; // when a section becomes active
  rootMarginBottom?: string;
}

export function useSectionObserver(
  providedSections: Section[] | Ref<Section[]> | ComputedRef<Section[]>,
  options: Options = {},
) {
  const { scrollOffset = 0, rootMarginTop = '-20%', rootMarginBottom = '-5%' } = options;

  const activeSection = ref<string | null>(null);
  const observer = ref<IntersectionObserver | null>(null);
  const observedElements = ref<HTMLElement[]>([]);
  const intersectingSections = ref<Set<string>>(new Set());

  const getSections = () => (isRef(providedSections) ? providedSections.value : providedSections);

  const scrollToSection = (slug: string) => {
    if (process.server) return;

    const el = document.getElementById(slug);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    const targetY = rect.top + scrollTop - scrollOffset;

    window.scrollTo({
      top: targetY,
      behavior: 'smooth',
    });
  };

  const setupObserver = async () => {
    if (process.server) return; // SSR guard

    await nextTick();

    // Clear previous observer
    observer.value?.disconnect();

    observer.value = new IntersectionObserver(
      (entries) => {
        // Track which sections are currently intersecting
        for (const entry of entries) {
          if (entry.isIntersecting) {
            intersectingSections.value.add(entry.target.id);
          } else {
            intersectingSections.value.delete(entry.target.id);
          }
        }

        // Set active to the topmost visible section
        if (intersectingSections.value.size > 0) {
          let topmost: HTMLElement | null = null;
          let topmostDistance = Infinity;

          const sectionIds = Array.from(intersectingSections.value);
          for (const id of sectionIds) {
            const el = document.getElementById(id);
            if (el) {
              const distance = el.getBoundingClientRect().top;
              if (distance < topmostDistance) {
                topmostDistance = distance;
                topmost = el;
              }
            }
          }

          if (topmost) {
            activeSection.value = topmost.id;
          }
        }
      },
      {
        rootMargin: `${rootMarginTop} 0px ${rootMarginBottom} 0px`,
        threshold: 0,
      },
    );

    // Observe each section
    const sections = getSections();
    observedElements.value = sections.map((s) => document.getElementById(s.slug)).filter(Boolean) as HTMLElement[];

    observedElements.value.forEach((el) => observer.value?.observe(el));
  };

  if (isRef(providedSections)) {
    watch(providedSections, () => setupObserver(), { deep: true, immediate: true });
  } else {
    onMounted(() => setupObserver());
  }

  onUnmounted(() => {
    observer.value?.disconnect();
  });

  return {
    activeSection,
    scrollToSection,
  };
}
